import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CountryStateCitySelect, { type GeoSelection } from "../../components/ui/CountryStateCitySelect";
import { boostService } from "../../features/boost/service";
import { dealsService } from "../../features/deals/service";
import type { Wallet } from "../../features/boost/types";
import type { CategoryV2, Deal } from "../../features/deals/types";
import { parseApiError } from "../../lib/http/apiErrors";

const SURFACES = [
  { id: "search", label: "Search results", desc: "Appear at top when buyers search your destination" },
  { id: "browse", label: "Browse & category pages", desc: "Featured position in deal listing pages" },
  { id: "notify", label: "Notification", desc: "Send an in-app notification to targeted users when the campaign goes live" },
];


const DURATION_OPTIONS = [3, 7, 14, 30];

const EMPTY_GEO_SEL: GeoSelection = {
  countryIso2: "", countryName: "", stateIso2: "", stateName: "",
  cityName: "", phoneCode: "", currency: "", currencyName: "", currencySymbol: "",
};
function fmtCredits(n: number) { return `${n.toLocaleString("en-IN")} credits`; }

declare global {
  interface Window { Razorpay: new (opts: Record<string, unknown>) => { open(): void }; }
}

export default function BoostWizardPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const dealId = params.get("deal_id") ?? "";
  const collection = params.get("collection") ?? "";
  const dealTitle = params.get("title") ?? "Selected deal";

  const [step, setStep] = useState(1);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [deal, setDeal] = useState<Deal | null>(null);
  const [allCategories, setAllCategories] = useState<CategoryV2[]>([]);
  const [dealCategories, setDealCategories] = useState<CategoryV2[]>([]);
  // `pricing` is stripped from the top-level single-deal response — fall back
  // to `ai_extracted_raw.pricing` (populated for older deals too).
  const dealPricing = useMemo(() => {
    const aiRaw = (deal as unknown as Record<string, unknown> | null)?.ai_extracted_raw as Record<string, unknown> | undefined;
    return (deal?.pricing ?? aiRaw?.pricing) as { currency?: string; original_price?: number; discounted_price?: number } | undefined;
  }, [deal]);

  // Step 2 — audience
  const [surfaces, setSurfaces] = useState<string[]>(["search", "browse"]);
  // test_phone was WhatsApp-only (now in-app notifications) — kept null in the payload.
  const [testPhone] = useState("");
  const [targetAudience, setTargetAudience] = useState<"seller" | "buyer" | "both">("buyer");
  const [targetTags, setTargetTags] = useState<string[]>([]);
  const [targetCountries, setTargetCountries] = useState<string[]>([]);
  const [targetCities, setTargetCities] = useState<string[]>([]);
  const [geoEnabled, setGeoEnabled] = useState(true);
  const [geoSel, setGeoSel] = useState<GeoSelection>(EMPTY_GEO_SEL);

  // Step 3 — budget + caps
  const [dailyBudgetCredits, setDailyBudgetCredits] = useState(500);
  // Max paid for ONE delivered impression. The auction charges only enough to
  // beat the next advertiser, so the actual price is usually LOWER than this.
  const [bidCredits, setBidCredits] = useState(10);
  const [durationDays, setDurationDays] = useState(7);
  const [maxImpressions] = useState<number | null>(null);
  const [maxClicks] = useState<number | null>(null);
  const [maxSearches] = useState<number | null>(null);
  const [payEnabled, setPayEnabled] = useState<string[]>([]);
  const [payShowPicker, setPayShowPicker] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<string>("");

  const totalCredits = dailyBudgetCredits * durationDays;
  const needsTopup = wallet ? wallet.balance_credits < totalCredits : false;
  const shortfallCredits = wallet ? Math.max(0, totalCredits - wallet.balance_credits) : 0;
  const ratePaise = (() => {
    const m = (wallet?.credit_rate_display ?? "").match(/₹([\d.]+)/);
    return m ? Math.round(parseFloat(m[1]) * 100) : 100;
  })();
  const fmtInr = (credits: number) =>
    `₹${((credits * ratePaise) / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

  useEffect(() => {
    if (!dealId || !collection) { navigate("/inventory/my"); return; }
    boostService.getWallet()
      .then(setWallet)
      .catch(() => setWallet(null))
      .finally(() => setLoadingWallet(false));

    boostService.getPaymentOptions()
      .then((opts) => {
        setPayEnabled(opts.enabled);
        setPayShowPicker(opts.show_picker);
        setSelectedGateway(opts.default || opts.enabled[0] || "");
      })
      .catch(() => { setPayEnabled([]); setPayShowPicker(false); });

    Promise.all([
      dealsService.getDeal(collection, dealId).catch(() => null),
      dealsService.listDealCategories().catch(() => []),
    ]).then(([fetchedDeal, cats]) => {
      setAllCategories(cats);
      if (!fetchedDeal) return;
      setDeal(fetchedDeal);

      // `destination`/`categories` live only in `ai_extracted_raw` on the
      // single-deal response — the top-level keys are stripped server-side.
      const aiRaw = (fetchedDeal as unknown as Record<string, unknown>).ai_extracted_raw as Record<string, unknown> | undefined;
      const dealDestination = aiRaw?.destination as { countries?: string[]; cities?: string[]; country?: string; city?: string } | undefined;

      // Pre-fill geo targeting — the seller's selling coverage. Combines this
      // deal's destination with the seller's saved market preferences
      // (seller_preferences), de-duplicated case-insensitively. Editable: the
      // seller can remove these or add the audience market they want to reach.
      const dedupe = (arr: string[]) => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const v of arr) {
          const k = v.trim().toLowerCase();
          if (v.trim() && !seen.has(k)) { seen.add(k); out.push(v.trim()); }
        }
        return out;
      };
      const dealCountries = dealDestination?.countries?.length
        ? dealDestination.countries
        : [dealDestination?.country ?? fetchedDeal.dealer_country ?? ""].filter(Boolean);
      const dealCities = dealDestination?.cities?.length
        ? dealDestination.cities
        : [dealDestination?.city ?? fetchedDeal.dealer_city ?? ""].filter(Boolean);
      const sellerCountries = user?.seller_preferences?.countries ?? [];
      const sellerCities = user?.seller_preferences?.cities ?? [];
      setTargetCountries(dedupe([...dealCountries, ...sellerCountries]));
      setTargetCities(dedupe([...dealCities, ...sellerCities]));

      // Derive deal-specific categories
      const dealCats = aiRaw?.categories as Record<string, unknown> | undefined;
      const dealSlugs = new Set([
        dealCats?.primary_slug,
        ...((dealCats?.secondary_slugs as string[] | undefined) ?? []),
        ...((dealCats?.subcategory_slugs as string[] | undefined) ?? []),
      ].filter(Boolean));

      const matched = cats.filter(c => dealSlugs.has(c.slug));
      setDealCategories(matched.length ? matched : cats);
      setTargetTags(matched.map(c => c.slug));
    });
  }, [dealId, collection, navigate]);

  function toggleSurface(id: string) {
    setSurfaces(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  }

  function toggleTag(tag: string) {
    setTargetTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  }

  // Commit the picker's current country/city selection into the target chips.
  // Values come straight from the canonical geo dataset, so they match deal
  // destinations + buyer preferences exactly (no spelling drift).
  function addGeoSelection() {
    const addUnique = (val: string, setList: Dispatch<SetStateAction<string[]>>) => {
      const v = val.trim();
      if (v) setList(prev => (prev.some(x => x.toLowerCase() === v.toLowerCase()) ? prev : [...prev, v]));
    };
    addUnique(geoSel.countryName, setTargetCountries);
    addUnique(geoSel.cityName, setTargetCities);
    setGeoSel(EMPTY_GEO_SEL);
  }

  function removeGeoChip(
    val: string,
    setList: Dispatch<SetStateAction<string[]>>,
  ) {
    setList(prev => prev.filter(x => x !== val));
  }

  async function handleActivate() {
    setError("");
    setSubmitting(true);
    try {
      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + durationDays);

      await boostService.createCampaign({
        deal_id: dealId,
        collection,
        surfaces,
        target_audience: targetAudience,
        target_countries: geoEnabled ? targetCountries : [],
        target_cities: geoEnabled ? targetCities : [],
        target_tags: targetTags,
        bid_credits: bidCredits,
        daily_budget_credits: dailyBudgetCredits,
        total_budget_credits: totalCredits,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        max_impressions: maxImpressions || null,
        max_clicks: maxClicks || null,
        max_search_appearances: maxSearches || null,
        test_phone: testPhone.trim() || null,
      });
      navigate("/boost/campaigns?activated=1");
    } catch (e: unknown) {
      setError(parseApiError(e, "Something went wrong").message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTopupAndActivate() {
    setError("");
    setSubmitting(true);
    try {
      const order = await boostService.topupInitiate(Math.max(shortfallCredits, 500), {
        deal_id: dealId,
        collection,
        surfaces,
        target_audience: targetAudience,
        target_countries: geoEnabled ? targetCountries : [],
        target_cities: geoEnabled ? targetCities : [],
        target_tags: targetTags,
        bid_credits: bidCredits,
        daily_budget_credits: dailyBudgetCredits,
        total_budget_credits: totalCredits,
        duration_days: durationDays,
        max_impressions: maxImpressions || null,
        max_clicks: maxClicks || null,
        max_search_appearances: maxSearches || null,
        test_phone: testPhone.trim() || null,
      }, payShowPicker ? selectedGateway || undefined : undefined);

      if (order.provider === "stripe") {
        window.location.href = order.checkout_url;
        return;
      }

      if (order.provider === "cashfree") {
        // Cashfree hosted checkout via the v3 JS SDK — consumes the
        // payment_session_id returned by the PG Orders API. The wallet is
        // credited server-side by the /payments/cashfree/webhook dispatcher.
        const w = window as unknown as {
          Cashfree?: (opts: { mode: string }) => {
            checkout: (opts: { paymentSessionId: string; redirectTarget?: string }) => Promise<unknown>;
          };
        };
        if (!w.Cashfree) {
          await new Promise<void>((resolve, reject) => {
            const s = document.createElement("script");
            s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
            s.onload = () => resolve();
            s.onerror = () => reject(new Error("Failed to load Cashfree"));
            document.body.appendChild(s);
          });
        }
        if (!w.Cashfree || !order.payment_session_id) {
          throw new Error("Cashfree checkout unavailable");
        }
        const cashfree = w.Cashfree({ mode: order.mode === "production" ? "production" : "sandbox" });
        await cashfree.checkout({
          paymentSessionId: order.payment_session_id,
          redirectTarget: "_self",
        });
        return;
      }

      // Razorpay: inline checkout widget
      if (!window.Razorpay) {
        await new Promise<void>((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://checkout.razorpay.com/v1/checkout.js";
          s.onload = () => resolve();
          s.onerror = () => reject(new Error("Failed to load Razorpay"));
          document.body.appendChild(s);
        });
      }

      await new Promise<void>((resolve, reject) => {
        const rz = new window.Razorpay({
          key: order.key_id,
          amount: order.amount_paise,
          currency: order.currency,
          name: "Gullivr — Boost Wallet",
          description: `Top up wallet to boost: ${dealTitle}`,
          order_id: order.order_id,
          handler: async (response: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
            try {
              const w = await boostService.topupConfirm({
                order_id: response.razorpay_order_id,
                payment_id: response.razorpay_payment_id,
                signature: response.razorpay_signature,
              });
              setWallet(w);
              resolve();
            } catch {
              reject(new Error("Payment verification failed"));
            }
          },
          modal: { ondismiss: () => reject(new Error("cancelled")) },
          theme: { color: "#2563EB" },
        });
        rz.open();
      });

      // After Razorpay top-up completes, activate campaign
      await handleActivate();
    } catch (e: unknown) {
      const err = e as Error;
      if (err.message !== "cancelled") {
        setError(err.message ?? "Payment failed. Try again.");
      }
      setSubmitting(false);
    }
  }

  const canProceed = {
    1: !!dealId && !!collection,
    2: surfaces.length > 0,
    3: dailyBudgetCredits >= 200 && durationDays >= 1,
  } as Record<number, boolean>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center gap-3">
        <button onClick={() => step > 1 ? setStep(s => s - 1) : navigate("/inventory/my")}
          className="text-gray-500 hover:text-gray-700 text-sm font-medium">
          ← Back
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold text-gray-900">Boost Deal</h1>
          <p className="text-xs text-gray-500">Step {step} of 4</p>
        </div>
        {/* Progress dots */}
        <div className="flex gap-1.5">
          {[1,2,3,4].map(n => (
            <div key={n} className={`w-2 h-2 rounded-full ${n <= step ? "bg-blue-600" : "bg-gray-200"}`} />
          ))}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">

        {/* ── STEP 1 — Deal confirmation ── */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Boost this deal</h2>
              <p className="text-sm text-gray-500 mt-1">Confirm the deal you want to promote</p>
            </div>
            <div className="bg-white rounded-xl border border-blue-200 p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-xl flex-shrink-0">⚡</div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{dealTitle}</p>
                  <p className="text-xs text-gray-400 mt-0.5 font-mono">{dealId}</p>
                </div>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
              <p className="text-xs text-amber-800">
                <strong>How it works:</strong> Your deal appears at the top of relevant search results and browse pages with a "Featured" label. You only pay for impressions served.
              </p>
            </div>
            <button onClick={() => setStep(2)} disabled={!canProceed[1]}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              Continue →
            </button>
          </div>
        )}

        {/* ── STEP 2 — Surfaces & Audience ── */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Reach & Audience</h2>
              <p className="text-sm text-gray-500 mt-1">Choose where your deal appears and who should see it</p>
            </div>

            {/* Where to appear */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Where to appear</p>
              {SURFACES.map(s => (
                <label key={s.id} className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={surfaces.includes(s.id)}
                    onChange={() => toggleSurface(s.id)}
                    className="mt-0.5 accent-blue-600" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{s.label}</p>
                    <p className="text-xs text-gray-500">{s.desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* In-app notification note — shown when notify surface selected */}
            {surfaces.includes("notify") && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-xs text-blue-700">
                  A notification is sent to users matching your <span className="font-semibold">Show to</span> audience and location filter when the campaign goes live.
                </p>
              </div>
            )}

            {/* Show to: Seller / Both / Buyer */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Show to</p>
              <div className="flex gap-2">
                {(["seller", "both", "buyer"] as const).map(opt => (
                  <button
                    key={opt}
                    onClick={() => setTargetAudience(opt)}
                    className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors capitalize ${
                      targetAudience === opt
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-300 text-gray-600 hover:border-blue-400"
                    }`}
                  >
                    {opt === "both" ? "Sellers & Buyers" : opt === "seller" ? "Sellers only" : "Buyers only"}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400">
                {targetAudience === "seller" && "Boost visible only to other sellers browsing the marketplace."}
                {targetAudience === "buyer" && "Boost visible only to buyers searching for deals."}
                {targetAudience === "both" && "Boost visible to all users — sellers and buyers."}
              </p>
            </div>

            {/* Deal categories */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Deal categories</p>
              <div className="flex flex-wrap gap-2">
                {dealCategories.map(cat => (
                  <button key={cat.slug} onClick={() => toggleTag(cat.slug)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      targetTags.includes(cat.slug)
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
                    }`}>
                    {cat.name}
                  </button>
                ))}
                {dealCategories.length === 0 && allCategories.length === 0 && (
                  <p className="text-xs text-gray-400">Loading categories…</p>
                )}
              </div>
              <p className="text-xs text-gray-400">Categories auto-selected from your deal. Deselect to broaden reach.</p>
            </div>

            {/* Geo targeting — canonical country → state → city picker.
                Values come from the shared /api/v1/geo dataset, identical to
                what deal destinations + buyer preferences store, so audience
                matching is exact (no "Bangalore" vs "Bengaluru" drift). */}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Geolocation targeting</p>
                <button
                  onClick={() => setGeoEnabled(v => !v)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${geoEnabled ? "bg-blue-600" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${geoEnabled ? "translate-x-4" : "translate-x-1"}`} />
                </button>
              </div>
              {!geoEnabled ? (
                <p className="text-xs text-gray-400">Disabled — boost reaches all locations.</p>
              ) : (
                <div className="space-y-3">
                  <CountryStateCitySelect
                    value={geoSel}
                    onChange={setGeoSel}
                    showCurrency={false}
                  />
                  <button
                    type="button"
                    disabled={!geoSel.countryName}
                    onClick={addGeoSelection}
                    className="w-full py-2 rounded-lg text-xs font-semibold border border-blue-600 text-blue-700 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    + Add to targeting
                  </button>

                  {targetCountries.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Countries</p>
                      <div className="flex flex-wrap gap-1.5">
                        {targetCountries.map(c => (
                          <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-200">
                            {c}
                            <button onClick={() => removeGeoChip(c, setTargetCountries)} className="text-indigo-400 hover:text-indigo-700 leading-none">×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {targetCities.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Cities</p>
                      <div className="flex flex-wrap gap-1.5">
                        {targetCities.map(c => (
                          <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-sky-50 text-sky-700 border border-sky-200">
                            {c}
                            <button onClick={() => removeGeoChip(c, setTargetCities)} className="text-sky-400 hover:text-sky-700 leading-none">×</button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-400">Pick the markets (audience location) you want this boost to reach. No locations = everywhere.</p>
                </div>
              )}
            </div>

            {deal && (dealPricing?.discounted_price ?? deal.discounted_price ?? dealPricing?.original_price ?? deal.original_price) ? (
              <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pricing</p>
                <div className="flex items-center gap-3">
                  {(dealPricing?.discounted_price ?? deal.discounted_price) ? (
                    <>
                      <span className="text-lg font-bold text-green-700">
                        {dealPricing?.currency ?? "₹"}{(dealPricing?.discounted_price ?? deal.discounted_price)?.toLocaleString("en-IN")}
                      </span>
                      {(dealPricing?.original_price ?? deal.original_price) && (
                        <span className="text-sm line-through text-gray-400">
                          {dealPricing?.currency ?? "₹"}{(dealPricing?.original_price ?? deal.original_price)?.toLocaleString("en-IN")}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-lg font-bold text-gray-800">
                      {dealPricing?.currency ?? "₹"}{(dealPricing?.original_price ?? deal.original_price)?.toLocaleString("en-IN")}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-400">Boost score factors in deal pricing to maximise buyer conversion.</p>
              </div>
            ) : null}

            <button onClick={() => setStep(3)} disabled={!canProceed[2]}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              Continue →
            </button>
          </div>
        )}

        {/* ── STEP 3 — Budget & Duration ── */}
        {step === 3 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Budget & Duration</h2>
              <p className="text-sm text-gray-500 mt-1">Set your daily spend and how long to run</p>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                  Daily Budget
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={200}
                    step={100}
                    value={dailyBudgetCredits}
                    onChange={e => setDailyBudgetCredits(Math.max(200, Number(e.target.value)))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <span className="text-xs text-gray-400">credits/day</span>
                </div>
                <div className="flex gap-2 mt-2">
                  {[200, 500, 1000, 2000].map(v => (
                    <button key={v} onClick={() => setDailyBudgetCredits(v)}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        dailyBudgetCredits === v ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 text-gray-600 hover:border-blue-400"
                      }`}>
                      {v} credits
                    </button>
                  ))}
                </div>
                {wallet?.credit_rate_display && (
                  <p className="text-xs text-gray-400 mt-1">{wallet.credit_rate_display}</p>
                )}
                {dailyBudgetCredits < 200 && (
                  <p className="text-xs text-red-500 mt-1">Minimum 200 credits/day</p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                  Max bid per view
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={bidCredits}
                    onChange={e => setBidCredits(Math.max(1, Number(e.target.value)))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                  <span className="text-xs text-gray-400">credits/view</span>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  You're charged only when your deal is actually shown — and only
                  enough to outrank the next advertiser, so you usually pay{" "}
                  <span className="font-medium">less than your max bid</span>. With
                  no competition you pay the minimum.
                </p>
                <p className="text-xs text-blue-600 font-medium mt-1">
                  ≈ {Math.max(0, Math.floor(dailyBudgetCredits / Math.max(1, bidCredits)))} views/day
                  at your max bid
                </p>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-2">
                  Duration
                </label>
                <div className="flex gap-2">
                  {DURATION_OPTIONS.map(d => (
                    <button key={d} onClick={() => setDurationDays(d)}
                      className={`flex-1 py-2 rounded-lg text-xs font-semibold border transition-colors ${
                        durationDays === d ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 text-gray-600 hover:border-blue-400"
                      }`}>
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Summary card */}
            <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Daily budget</span>
                <span className="font-semibold text-gray-900">
                  {fmtCredits(dailyBudgetCredits)}/day
                  <span className="text-xs text-gray-400 ml-1">≈ {fmtInr(dailyBudgetCredits)}</span>
                </span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Duration</span>
                <span className="font-semibold text-gray-900">{durationDays} days</span>
              </div>
              <div className="border-t border-blue-200 mt-2 pt-2 flex justify-between items-baseline">
                <span className="text-sm font-semibold text-gray-700">Total</span>
                <span className="text-right">
                  <span className="text-lg font-bold text-blue-700">{fmtCredits(totalCredits)}</span>
                  <span className="text-xs text-blue-400 ml-1">≈ {fmtInr(totalCredits)}</span>
                </span>
              </div>
            </div>

            <button onClick={() => setStep(4)} disabled={!canProceed[3]}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors">
              Review →
            </button>
          </div>
        )}

        {/* ── STEP 4 — Review & Pay ── */}
        {step === 4 && (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Review & Activate</h2>
              <p className="text-sm text-gray-500 mt-1">Confirm your campaign details</p>
            </div>

            {/* Summary */}
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-gray-500">Deal</span>
                <span className="font-medium text-gray-900 text-right max-w-[60%] truncate">{dealTitle}</span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-gray-500">Surfaces</span>
                <span className="font-medium text-gray-900">{surfaces.join(", ")}</span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-gray-500">Show to</span>
                <span className="font-medium text-gray-900 capitalize">
                  {targetAudience === "both" ? "Sellers & Buyers" : targetAudience === "seller" ? "Sellers only" : "Buyers only"}
                </span>
              </div>
              {(targetCountries.length > 0 || targetCities.length > 0) && geoEnabled && (
                <div className="px-4 py-3 flex justify-between text-sm">
                  <span className="text-gray-500">Geo</span>
                  <span className="font-medium text-gray-900 text-right max-w-[60%]">
                    {[...targetCountries, ...targetCities].join(", ")}
                  </span>
                </div>
              )}
              {targetTags.length > 0 && (
                <div className="px-4 py-3 flex justify-between text-sm">
                  <span className="text-gray-500">Categories</span>
                  <span className="font-medium text-gray-900 text-right max-w-[60%]">{targetTags.join(", ")}</span>
                </div>
              )}
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-gray-500">Daily budget</span>
                <span className="font-medium text-gray-900">{fmtCredits(dailyBudgetCredits)}/day</span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-gray-500">Duration</span>
                <span className="font-medium text-gray-900">{durationDays} days</span>
              </div>
              <div className="px-4 py-3 flex justify-between">
                <span className="text-sm font-bold text-gray-900">Total</span>
                <span className="text-base font-bold text-blue-700">{fmtCredits(totalCredits)}</span>
              </div>
            </div>

            {/* Wallet status */}
            <div className={`rounded-xl border p-4 ${needsTopup ? "bg-amber-50 border-amber-200" : "bg-green-50 border-green-200"}`}>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Wallet balance</span>
                <span className={`text-sm font-bold ${needsTopup ? "text-amber-700" : "text-green-700"}`}>
                  {loadingWallet ? "…" : fmtCredits(wallet?.balance_credits ?? 0)}
                </span>
              </div>
              {needsTopup && (
                <p className="text-xs text-amber-700 mt-1">
                  Need {fmtCredits(shortfallCredits)} more to cover first day's budget. We'll top up automatically.
                </p>
              )}
              {!needsTopup && (
                <p className="text-xs text-green-700 mt-1">Sufficient balance — campaign activates instantly.</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {needsTopup && payShowPicker && payEnabled.length > 1 && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <p className="text-xs font-semibold text-gray-700 mb-2">Payment method</p>
                <div className="flex flex-wrap gap-2">
                  {payEnabled.map((gw) => (
                    <button
                      key={gw}
                      type="button"
                      onClick={() => setSelectedGateway(gw)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        selectedGateway === gw
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-700 hover:border-gray-400"
                      }`}
                    >
                      {gw.charAt(0).toUpperCase() + gw.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={needsTopup ? handleTopupAndActivate : handleActivate}
              disabled={submitting || loadingWallet || surfaces.length === 0}
              className="w-full py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              {submitting
                ? "Activating…"
                : needsTopup
                ? `⚡ Top Up & Activate Boost`
                : `⚡ Activate Boost`}
            </button>
            <p className="text-xs text-center text-gray-400">
              Nothing is charged upfront. You pay only when your deal is actually shown —
              at most {fmtCredits(bidCredits)}/view, and never more than {fmtCredits(dailyBudgetCredits)} in a day.
              Once the daily cap is reached the campaign pauses and resumes the next day.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
